import { router, usePage } from '@inertiajs/react'
import Navbar from '@/components/layout/Navbar'
import EpisodeCard from '@/components/ui/EpisodeCard'
import { cn } from '@/lib/utils'
import type { EpisodeCard as EpisodeCardType, Tag, SharedProps } from '@/types'
import { Link } from '@inertiajs/react'

interface Props {
  episodes: EpisodeCardType[]
  tags: (Tag & { count: number })[]
  filters: { difficulty?: string; free?: string; tag?: string; sort?: string }
}

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'] as const

export default function Index({ episodes, tags, filters }: Props) {
  const { auth } = usePage<SharedProps>().props

  const setFilter = (key: string, value: string | null) => {
    const params: Record<string, string> = {}
    const current = { ...filters, [key]: value ?? undefined }
    Object.entries(current).forEach(([k, v]) => { if (v) params[k] = v })
    router.get('/episodes', params, { preserveScroll: true })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-4">
          <Link href="/" className="font-bold text-xl text-gray-900">GetCasts</Link>
          <div className="flex-1" />
          {auth.user ? (
            <Link href="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">Dashboard</Link>
          ) : (
            <Link
              href="/pricing"
              className="px-4 py-1.5 bg-brand text-white text-sm font-medium rounded-full"
            >
              Pro $9/mo
            </Link>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar */}
        <aside className="w-44 shrink-0">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">TOPICS</p>
          <ul className="space-y-1 mb-8">
            {tags.map(tag => (
              <li key={tag.slug}>
                <button
                  onClick={() => setFilter('tag', filters.tag === tag.slug ? null : tag.slug)}
                  className={cn(
                    'w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm text-left transition-colors',
                    filters.tag === tag.slug
                      ? 'bg-brand/10 text-brand font-semibold'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <span>{tag.name}</span>
                  <span className={cn('text-xs', filters.tag === tag.slug ? 'text-brand' : 'text-gray-400')}>
                    {tag.count}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3 mt-8">ACCESS</p>
          <button
            onClick={() => setFilter('free', filters.free === 'true' ? null : 'true')}
            className={cn(
              'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left transition-colors',
              filters.free === 'true'
                ? 'bg-green-50 text-green-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
            Free only
          </button>

          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3 mt-8">DIFFICULTY</p>
          <ul className="space-y-1">
            {DIFFICULTIES.map(d => (
              <li key={d} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={d}
                  checked={filters.difficulty === d}
                  onChange={() => setFilter('difficulty', filters.difficulty === d ? null : d)}
                  className="rounded border-gray-300 text-brand focus:ring-brand/30"
                />
                <label htmlFor={d} className="text-sm text-gray-600 capitalize cursor-pointer">{d}</label>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main */}
        <div className="flex-1">
          {/* Header + sort */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">All Episodes</h1>
            <div className="flex gap-2">
              {(['newest', 'oldest'] as const).map(opt => (
                <button
                  key={opt}
                  onClick={() => setFilter('sort', opt)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-lg border transition-colors capitalize',
                    filters.sort === opt || (!filters.sort && opt === 'newest')
                      ? 'border-gray-300 bg-white text-gray-700'
                      : 'border-transparent text-gray-400 hover:text-gray-700'
                  )}
                >
                  {opt === 'newest' ? 'Newest ↓' : 'Oldest ↑'}
                </button>
              ))}
            </div>
          </div>

          {episodes.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No episodes found.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((ep, i) => (
                <EpisodeCard key={ep.id} episode={ep} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
